import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Facility, Anchor, Zone } from '../../types';
import api from '../../services/api';

interface FacilitiesState {
  facilities: Facility[];
  currentFacility: Facility | null;
  anchors: Anchor[];
  zones: Zone[];
  loading: boolean;
  error: string | null;
}

const initialState: FacilitiesState = {
  facilities: [],
  currentFacility: null,
  anchors: [],
  zones: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchFacilities = createAsyncThunk(
  'facilities/fetchFacilities',
  async () => {
    const facilities = await api.getFacilities();
    return facilities;
  }
);

/** @deprecated Use fetchFacilities instead */
export const fetchSites = fetchFacilities;

export const fetchFacility = createAsyncThunk(
  'facilities/fetchFacility',
  async (facilityId: string) => {
    const facility = await api.getFacility(facilityId);
    return facility;
  }
);

/** @deprecated Use fetchFacility instead */
export const fetchSite = fetchFacility;

export const fetchFacilityAnchors = createAsyncThunk(
  'facilities/fetchFacilityAnchors',
  async (facilityId: string) => {
    const anchors = await api.getAnchors(facilityId);
    return anchors;
  }
);

/** @deprecated Use fetchFacilityAnchors instead */
export const fetchSiteBeacons = fetchFacilityAnchors;

export const fetchFacilityZones = createAsyncThunk(
  'facilities/fetchFacilityZones',
  async () => {
    const zones = await api.getFacilities();  // This should probably be getZones but using getFacilities for now
    return zones;
  }
);

/** @deprecated Use fetchFacilityZones instead */
export const fetchSiteZones = fetchFacilityZones;

const facilitiesSlice = createSlice({
  name: 'facilities',
  initialState,
  reducers: {
    setFacilities: (state, action: PayloadAction<Facility[]>) => {
      state.facilities = action.payload;
      state.loading = false;
      state.error = null;
    },

    addFacility: (state, action: PayloadAction<Facility>) => {
      state.facilities.push(action.payload);
    },

    updateFacility: (state, action: PayloadAction<Facility>) => {
      const index = state.facilities.findIndex(f => f.id === action.payload.id);
      if (index !== -1) {
        state.facilities[index] = action.payload;
      }

      if (state.currentFacility?.id === action.payload.id) {
        state.currentFacility = action.payload;
      }
    },

    removeFacility: (state, action: PayloadAction<string>) => {
      state.facilities = state.facilities.filter(f => f.id !== action.payload);

      if (state.currentFacility?.id === action.payload) {
        state.currentFacility = null;
      }
    },

    setCurrentFacility: (state, action: PayloadAction<Facility | null>) => {
      state.currentFacility = action.payload;
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },

    setAnchors: (state, action: PayloadAction<Anchor[]>) => {
      state.anchors = action.payload;
    },

    setZones: (state, action: PayloadAction<Zone[]>) => {
      state.zones = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch facilities
    builder
      .addCase(fetchFacilities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFacilities.fulfilled, (state, action) => {
        state.loading = false;
        state.facilities = action.payload || [];
      })
      .addCase(fetchFacilities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch facilities';
      });

    // Fetch facility
    builder
      .addCase(fetchFacility.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFacility.fulfilled, (state, action) => {
        state.loading = false;
        state.currentFacility = action.payload;
      })
      .addCase(fetchFacility.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch facility';
      });

    // Fetch facility anchors
    builder
      .addCase(fetchFacilityAnchors.fulfilled, (state, action) => {
        state.anchors = action.payload;
      });

    // Fetch facility zones
    builder
      .addCase(fetchFacilityZones.fulfilled, (state) => {
        state.zones = [];
      });
  },
});

export const {
  setFacilities,
  addFacility,
  updateFacility,
  removeFacility,
  setCurrentFacility,
  setLoading,
  setError,
  setAnchors,
  setZones,
} = facilitiesSlice.actions;

// Backward-compatible action aliases
export const setSites = setFacilities;
export const addSite = addFacility;
export const updateSite = updateFacility;
export const removeSite = removeFacility;
export const setCurrentSite = setCurrentFacility;
export const setBeacons = setAnchors;

export default facilitiesSlice.reducer;

// Root state shape after the store was migrated from `sites` to `facilities`.
interface RootWithFacilities {
  facilities: FacilitiesState;
}

// Selectors
export const selectAllFacilities = (state: RootWithFacilities) => state.facilities.facilities;
export const selectCurrentFacility = (state: RootWithFacilities) => state.facilities.currentFacility;
export const selectFacilityById = (facilityId: string) => (state: RootWithFacilities) =>
  state.facilities.facilities.find(f => f.id === facilityId);
export const selectFacilityAnchors = () => (state: RootWithFacilities) => state.facilities.anchors;
export const selectFacilityZones = () => (state: RootWithFacilities) => state.facilities.zones;
export const selectFacilitiesLoading = (state: RootWithFacilities) => state.facilities.loading;
export const selectFacilitiesError = (state: RootWithFacilities) => state.facilities.error;

// Backward-compatible selector aliases — keep old names working for un-migrated callers
export const selectAllSites = selectAllFacilities;
export const selectCurrentSite = selectCurrentFacility;
export const selectSiteById = selectFacilityById;
export const selectSiteBeacons = selectFacilityAnchors;
export const selectSiteZones = selectFacilityZones;
export const selectSitesLoading = selectFacilitiesLoading;
export const selectSitesError = selectFacilitiesError;
